import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, PaperPlaneTilt, QrCode } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';

const API = '/api';

const PublicCapturePortal = () => {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/capture/forms/${slug}`);
        setConfig(data);
      } catch (e) {
        console.error(e);
        toast.error('Erro ao carregar formulário');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();

    if (!form.phone && !form.email) {
      toast.error('Informe telefone ou e-mail para contato');
      return;
    }

    setSending(true);
    try {
      const { data } = await axios.post(`${API}/capture/forms/${slug}/submit`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        notes: form.notes,
        source: 'Public Capture Portal',
        enterprise_interest: config?.enterprise_interest
      });

      setSuccess(data);
      toast.success('Cadastro enviado com sucesso');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao enviar cadastro');
    } finally {
      setSending(false);
    }
  };

  const publicUrl = window.location.href;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(publicUrl)}`;

  const whatsappNumber = config?.whatsapp || '5577999999999';
  const whatsappText = encodeURIComponent('Olá, acabei de realizar meu cadastro e gostaria de falar com um especialista.');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappText}`;
  const empreendimentosUrl = config?.enterprise_url || config?.website || '#';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">Carregando...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-8 text-center">
          <CheckCircle size={68} className="mx-auto text-emerald-600 mb-4" weight="duotone" />
          <h1 className="text-3xl font-bold text-slate-900">Parabéns!</h1>
          <h2 className="text-xl font-bold text-slate-900 mt-2">Cadastro realizado com sucesso.</h2>

          <p className="text-slate-600 mt-4">
            Fale agora com um de nossos especialistas ou conheça melhor nossos empreendimentos.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition"
            >
              Conversar no WhatsApp
            </a>

            <a
              href={empreendimentosUrl}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800 transition"
            >
              Conhecer empreendimentos
            </a>
          </div>

          <p className="text-xs text-slate-400 mt-6">
            Seus dados foram recebidos e serão direcionados para o atendimento comercial.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative">
      <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-flex px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold mb-4">
            Atendimento Comercial
          </div>

          <h1 className="text-4xl font-bold text-slate-900">
            {config?.title || 'Fale com a nossa equipe'}
          </h1>

          <p className="text-lg text-slate-600 mt-4">
            {config?.subtitle || 'Preencha seus dados e receba atendimento especializado.'}
          </p>

          <div className="mt-8 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <QrCode size={26} className="text-slate-700" />
              <h2 className="font-bold text-slate-900">Captação rápida</h2>
            </div>

            <p className="text-sm text-slate-500 mb-4">
              Ideal para stands, feirões, recepção e ações presenciais. Abra o QR Code para o cliente preencher no próprio celular.
            </p>

            <button
              onClick={() => setShowQr(true)}
              className="px-5 py-3 rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800 transition"
            >
              Mostrar QR Code
            </button>

            <p className="text-xs text-slate-400 mt-3 break-all">{publicUrl}</p>
          </div>

          <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h2 className="font-bold text-slate-900 mb-2">Atendimento com segurança</h2>
            <p className="text-sm text-slate-500">
              Seus dados serão utilizados exclusivamente para contato comercial e apresentação de oportunidades.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Dados para contato</h2>
          <p className="text-sm text-slate-500 mb-5">
            Informe telefone ou e-mail para que nossa equipe possa entrar em contato.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Nome</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Seu nome"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone / WhatsApp</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="(77) 99999-9999"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">E-mail</label>
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="voce@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Conte rapidamente o que procura"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-60"
            >
              <PaperPlaneTilt size={20} />
              {sending ? 'Enviando...' : 'Enviar cadastro'}
            </button>
          </form>

          <p className="text-xs text-slate-400 mt-4">
            Seus dados serão utilizados exclusivamente para contato comercial e apresentação de oportunidades.
          </p>
        </div>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-7 w-full max-w-sm text-center">
            <QrCode size={40} className="mx-auto text-slate-900 mb-3" />
            <h2 className="text-xl font-bold text-slate-900">Escaneie para se cadastrar</h2>
            <p className="text-sm text-slate-500 mt-2">
              Aponte a câmera do celular para o QR Code abaixo.
            </p>

            <img src={qrUrl} alt="QR Code" className="w-56 h-56 mx-auto mt-5 rounded-lg border border-slate-200" />

            <p className="text-xs text-slate-400 mt-4 break-all">{publicUrl}</p>

            <button
              onClick={() => setShowQr(false)}
              className="mt-5 px-5 py-2 rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCapturePortal;
